import React, { FC, useEffect, useState } from 'react';
import { CircularProgress } from '@mui/material';

import { IDisplayDepartmentDTO } from 'src/api/dtos';
import {
  Autocomplete,
  ChevronDownIcon,
  CloseFilledIcon,
  DepartmentIcon,
  Modal,
  SearchIcon,
} from 'src/shared/components';
import { useTranslate } from 'src/shared/providers';

interface Props {
  departments: IDisplayDepartmentDTO[];
  loading: boolean;
  open: boolean;
  onClose: () => void;
  onSubmit: (value: string) => void;
}

export const DepartmentPunchModal: FC<Props> = ({ loading, open, onClose, onSubmit, departments }) => {
  const { t } = useTranslate();
  const [departmentSelection, setDepartmentSelection] = useState<IDisplayDepartmentDTO | undefined>();

  useEffect(() => {
    if (open) {
      return;
    }

    setDepartmentSelection(undefined);
  }, [open]);

  const handleSelection = (value: IDisplayDepartmentDTO | undefined) => {
    setDepartmentSelection(value);
  };

  const handleSubmit = () => {
    if (!departmentSelection) {
      return;
    }

    onSubmit(departmentSelection.number.toString());
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      modalHeaderProps={{
        title: t('punches.departmentPunch'),
        message: t('punches.toSubmitADepartmentPunchMakeSureToSelectAValueForTheDropdownBelow'),
        onClose,
      }}
      modalFooterProps={{
        closeText: t('global.close'),
        submitText: t('punches.submitDepartmentPunch'),
        closeIcon: <CloseFilledIcon />,
        submitIcon: <DepartmentIcon />,
        loading,
        disabled: !departmentSelection || loading,
        onClose,
        onSubmit: handleSubmit,
      }}
    >
      {!departments.length ? (
        <div className="my-5 d-flex align-items-center justify-content-center">
          <CircularProgress />
        </div>
      ) : (
        <SelectRow name={t('punches.department')} values={departments} onSelection={handleSelection} />
      )}
    </Modal>
  );
};

interface IDepartmentLevelProps {
  name: string;
  values: IDisplayDepartmentDTO[];
  onSelection: (value: IDisplayDepartmentDTO | undefined) => void;
}

const SelectRow: FC<IDepartmentLevelProps> = ({ values, name, onSelection }) => {
  const { t } = useTranslate();

  return (
    <div className="mb-3 w-75">
      <p className="mb-2 fw-medium required">{name}</p>
      <Autocomplete
        size="small"
        className="w-100"
        placeholder={t('global.selectValueOrSearchValue')}
        popupIcon={<ChevronDownIcon color="#C2C9D1" />}
        popupStartIcon={<SearchIcon />}
        onChange={(_, data) => onSelection(data || undefined)}
        options={values}
        getOptionLabel={({ number, formatDescription }) => `${number} - ${formatDescription}`}
      />
    </div>
  );
};
